"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from "react";

/**
 * How the browser authenticates to the runtime gateway (/gateway/*, /ws) for
 * the agent it is looking at (.plans/plan-member-credentials.md).
 *
 * That is a short-lived token naming THIS person (lib/member-credential.ts),
 * refreshed shortly before it expires through POST /api/instances/member-token
 * — which re-checks membership, so someone removed from the agent stops
 * getting tokens.
 *
 * Consumers never hold the token itself: they ask for it when they make a
 * request (`headers()`, `bearer()`, `socket()`), so a refresh changes
 * nothing anyone renders and re-runs no effect, and a socket that reconnects
 * after a laptop slept gets a fresh token rather than the one it opened with.
 */

/** As minted by the server: the token and when it stops working (epoch ms). */
export interface MemberCredential {
  readonly token: string;
  readonly expiresAt: number;
}

/** Ask for a new token this long before the current one expires. */
const REFRESH_MARGIN_MS = 60_000;

/** The subprotocol the gateway reads a member token from (runtime
 *  gateway/member-auth.ts) — the token rides a header, never the URL. */
const MEMBER_WS_PROTOCOL = "squidler.member";

export class GatewayAuthError extends Error {
  constructor(readonly status: number) {
    super(`member token refresh failed: HTTP ${String(status)}`);
    this.name = "GatewayAuthError";
  }
}

const isCredential = (v: unknown): v is MemberCredential =>
  v !== null && typeof v === "object"
  && "token" in v && typeof v.token === "string"
  && "expiresAt" in v && typeof v.expiresAt === "number";

const isFileLink = (v: unknown): v is { url: string; expiresAt: number } =>
  v !== null && typeof v === "object"
  && "url" in v && typeof v.url === "string"
  && "expiresAt" in v && typeof v.expiresAt === "number";

/** One agent's credentials in this tab. */
export class GatewayCredentials {
  // An external store, not React state: the token is read when a request is
  // made and never rendered, so changing it must not re-render anything.
  private readonly state: { current: MemberCredential; refreshing: Promise<MemberCredential> | null };
  /** Signed file links already minted, by (snapshot, path). */
  private readonly links = new Map<string, { url: string; expiresAt: number }>();
  /** Mints in flight, by the same key: several elements showing one file
   *  (a user's upload is a link around an image) share one request. */
  private readonly minting = new Map<string, Promise<string>>();

  constructor(
    readonly instanceId: string,
    initial: MemberCredential,
    private readonly now: () => number = Date.now,
    private readonly fetchFn: typeof fetch = (...args) => fetch(...args),
    /** Called when the refresh is refused for good — 401 (signed out) or 404
     *  (no longer a member). Retrying cannot help either; see reloadOnce. */
    private readonly onRefused: (status: 401 | 404) => void = () => {},
  ) {
    this.state = { current: initial, refreshing: null };
  }

  /** The token to send now — refreshed first when it is about to expire. */
  async bearer(): Promise<string> {
    const current = this.state.current;
    if (current.expiresAt - this.now() > REFRESH_MARGIN_MS) return current.token;
    return (await this.refresh()).token;
  }

  /** Ask for a fresh token now, whatever the one in hand says — when the
   *  runtime has said this person's access changed (their socket closed with
   *  "membership revoked"): a refusal then reloads at once (onRefused) rather
   *  than when the token next nears expiry. */
  revalidate(): void {
    void this.refresh().catch(() => {});
  }

  /** `Authorization` for a fetch to /gateway/*. */
  async headers(): Promise<Record<string, string>> {
    return { Authorization: `Bearer ${await this.bearer()}` };
  }

  /** What to open `new WebSocket(url, protocols)` with, for the page's chat
   *  URL: the member token as the subprotocol, never in the URL. */
  async socket(wsUrl: string): Promise<{ url: string; protocols: string[] }> {
    return { url: wsUrl, protocols: [MEMBER_WS_PROTOCOL, await this.bearer()] };
  }

  /**
   * A URL the browser can load one workspace file from (an `<img>`, a
   * download) — for the live file, or its copy in `snapshot`: a signed link
   * (runtime gateway/file-links.ts) for this person, this file, ten minutes;
   * kept and reused until a minute before it expires.
   */
  async fileLink(path: string, snapshot?: string): Promise<string> {
    const key = `${snapshot ?? ""}\u0000${path}`;
    const fresh = this.freshFileLink(path, snapshot);
    if (fresh !== null) return fresh;
    const inFlight = this.minting.get(key);
    if (inFlight) return inFlight;
    const minted = this.mintFileLink(key, path, snapshot).finally(() => { this.minting.delete(key); });
    this.minting.set(key, minted);
    return minted;
  }

  private async mintFileLink(key: string, path: string, snapshot: string | undefined): Promise<string> {
    const query = new URLSearchParams({ path, ...(snapshot !== undefined ? { snapshot } : {}) });
    const res = await this.fetchFn(`/gateway/files/link?${query.toString()}`, { headers: await this.headers(), cache: "no-store" });
    if (!res.ok) throw new GatewayAuthError(res.status);
    const body: unknown = await res.json();
    if (!isFileLink(body)) throw new GatewayAuthError(502);
    this.links.set(key, body);
    return body.url;
  }

  /** Forget a link that failed to load (expired, or the file changed hands),
   *  so the next `fileLink` mints a fresh one. */
  /** The link already in hand for this file, if it is good for more than a
   *  minute — synchronously, for a click that must not wait (useFileLink). */
  freshFileLink(path: string, snapshot?: string): string | null {
    const known = this.links.get(`${snapshot ?? ""}\u0000${path}`);
    return known && known.expiresAt - this.now() > REFRESH_MARGIN_MS ? known.url : null;
  }

  forgetFileLink(path: string, snapshot?: string): void {
    this.links.delete(`${snapshot ?? ""}\u0000${path}`);
  }

  /** A fresh credential — one refresh at a time, however many requests are
   *  waiting for it. */
  private refresh(): Promise<MemberCredential> {
    this.state.refreshing ??= this.fetchFn("/api/instances/member-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ instanceId: this.instanceId }),
      cache: "no-store",
    })
      .then(async (res) => {
        if (res.status === 401 || res.status === 404) this.onRefused(res.status);
        if (!res.ok) throw new GatewayAuthError(res.status);
        const body: unknown = await res.json();
        if (!isCredential(body)) throw new GatewayAuthError(502);
        this.state.current = body;
        return body;
      })
      .finally(() => { this.state.refreshing = null; });
    return this.state.refreshing;
  }
}

/** What the server hands the page for one agent. */
export interface GatewayAuthSeed {
  readonly id: string;
  readonly memberCredential: MemberCredential;
}

const RELOAD_MARKER = "sq_gateway_auth_reload";
const RELOAD_GUARD_MS = 60_000;

/**
 * A page whose token refresh is refused for good reloads — once. A 401 means
 * the session ended while the page stayed loaded (an installed PWA resumed
 * after its session's 14 / 30 days): the reload goes through the normal
 * sign-in redirect and back. A 404 means the person was removed from the
 * agent: the reload renders without it. Without this the chat retried
 * silently for ever. The marker stops a reload loop if a reloaded page is refused
 * again at once (it should not be: the server's own checks then decide).
 */
const reloadOnce = (instanceId: string): void => {
  const key = `${RELOAD_MARKER}:${instanceId}`;
  const last = (() => {
    try { return Number(window.sessionStorage.getItem(key) ?? "0"); } catch { return 0; }
  })();
  if (Date.now() - last < RELOAD_GUARD_MS) return;
  // Without storage the guard cannot hold, and a reload loop is worse than a
  // chat that keeps retrying: then do nothing.
  const marked = (() => {
    try { window.sessionStorage.setItem(key, String(Date.now())); return true; } catch { return false; }
  })();
  if (marked) window.location.reload();
};

/** Every agent this tab has looked at, each with its own credentials: switching
 *  agents and back keeps the token already fetched. Seeds only on first sight —
 *  after that the store keeps itself fresh, so a re-render with an older
 *  server-rendered token never replaces a newer one. */
class GatewayCredentialRegistry {
  private readonly byInstance = new Map<string, GatewayCredentials>();

  for(seed: GatewayAuthSeed): GatewayCredentials {
    const known = this.byInstance.get(seed.id);
    if (known) return known;
    const created = new GatewayCredentials(seed.id, seed.memberCredential, Date.now, (...args) => fetch(...args), () => { reloadOnce(seed.id); });
    this.byInstance.set(seed.id, created);
    return created;
  }
}

const GatewayAuthContext = createContext<GatewayCredentials | null>(null);

/** Provides the credentials of the agent on screen (none while no agent is).
 *  Mounted by InstanceShell, so everything under /a/<slug> has one. */
export function GatewayAuthProvider({ instance, children }: { instance: GatewayAuthSeed | undefined; children: ReactNode }) {
  const [registry] = useState(() => new GatewayCredentialRegistry());
  // Keyed on the id alone: the store is the same object for as long as the
  // agent is on screen, which is what keeps consumers' effects from re-running.
  const id = instance?.id;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- seeds are read on first sight only (see GatewayCredentialRegistry)
  const credentials = useMemo(() => (instance ? registry.for(instance) : null), [registry, id]);
  return <GatewayAuthContext.Provider value={credentials}>{children}</GatewayAuthContext.Provider>;
}

/** The credentials of the agent on screen. */
export function useGatewayAuth(): GatewayCredentials {
  const credentials = useContext(GatewayAuthContext);
  if (credentials === null) throw new Error("useGatewayAuth outside GatewayAuthProvider, or with no agent on screen");
  return credentials;
}

/** As useGatewayAuth, for the few consumers that also render with no agent on
 *  screen (the chat socket provider). */
export function useOptionalGatewayAuth(): GatewayCredentials | null {
  return useContext(GatewayAuthContext);
}

/**
 * Headers for a gateway request from code whose errors are a typed union
 * (chat uploads, dictation): `{}` with no agent on screen, else the member
 * token. A failed refresh is thrown in the shape those
 * unions already have — `rejected` with the refresh's status, or `network` —
 * so callers need no new branch.
 */
export async function gatewayHeaders(auth: GatewayCredentials | null): Promise<Record<string, string>> {
  if (!auth) return {};
  try {
    return await auth.headers();
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- the typed error unions of lib/uploads.ts and lib/dictation.ts (their idiom)
    throw err instanceof GatewayAuthError
      ? { kind: "rejected", status: err.status, reason: "member_token" }
      : { kind: "network", message: err instanceof Error ? err.message : "Network error" };
  }
}

/** A link that fails to load sooner than this after it was minted failed for
 *  some other reason than expiry (the file is gone): no new link for it. */
const RETRY_AFTER_MS = 60_000;

/**
 * A loadable URL for one workspace file (`<img src>`, a download `href`) —
 * a signed link, minted when needed (GatewayCredentials.fileLink). `path` null
 * = nothing to load. A link lives ten minutes, and a page stays open far
 * longer, so a URL rendered once goes stale:
 *
 * - `onError` on an element that loads by itself (`<img>`): a link that
 *   expired while the page was open is re-minted and the image loads again,
 *   once per stale link.
 * - `onClick` on an anchor (a download, a file opened in a new tab): checked
 *   at the click, which is when the link is used, and re-minted first when
 *   it has less than a minute left. Without it a download clicked more than
 *   ten minutes after the page rendered was a 401, which the browser shows
 *   as "file not available on site" (report b876254d).
 * - `version` for a source the page reloads itself (an iframe or image whose
 *   file changed): a new version resolves the link again rather than putting
 *   the new version on a stale one.
 */
export function useFileLink(
  path: string | null,
  snapshot?: string,
  version?: string,
): { url: string | null; onError: () => void; onClick: (e: MouseEvent<HTMLAnchorElement>) => void } {
  const auth = useGatewayAuth();
  const [generation, setGeneration] = useState(0);
  const [resolved, setResolved] = useState<{ key: string; url: string; at: number } | null>(null);
  const key = path === null ? null : `${auth.instanceId}\u0000${snapshot ?? ""}\u0000${path}\u0000${String(generation)}\u0000${version ?? ""}`;

  useEffect(() => {
    if (path === null || key === null) return;
    const state = { cancelled: false };
    auth.fileLink(path, snapshot).then(
      (url) => { if (!state.cancelled) setResolved({ key, url, at: Date.now() }); },
      // No link (offline, no longer a member): the element stays without a
      // source, as a missing file would.
      () => {},
    );
    return () => { state.cancelled = true; };
  }, [auth, path, snapshot, key]);

  const current = resolved !== null && resolved.key === key ? resolved : null;
  const onError = useCallback(() => {
    if (path === null || current === null || Date.now() - current.at < RETRY_AFTER_MS) return;
    auth.forgetFileLink(path, snapshot);
    setGeneration((n) => n + 1);
  }, [auth, path, snapshot, current]);

  const onClick = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    if (path === null || key === null) return;
    const anchor = e.currentTarget;
    const fresh = auth.freshFileLink(path, snapshot);
    if (fresh !== null) {
      // Good for more than a minute: let the click go ahead on it.
      anchor.href = fresh;
      return;
    }
    e.preventDefault();
    // A new tab must be opened inside the click or a popup blocker refuses
    // it; it is pointed at the file once the link is minted.
    const newTab = !anchor.hasAttribute("download") && (anchor.target === "_blank" || e.metaKey || e.ctrlKey || e.shiftKey);
    const tab = newTab ? window.open("", "_blank") : null;
    void auth.fileLink(path, snapshot).then(
      (url) => {
        setResolved({ key, url, at: Date.now() });
        if (tab) {
          tab.opener = null;
          tab.location.href = url;
          return;
        }
        // Clicked again with a fresh link, so this time it goes through.
        anchor.href = url;
        anchor.click();
      },
      () => { tab?.close(); },
    );
  }, [auth, path, snapshot, key]);

  return { url: current?.url ?? null, onError, onClick };
}

/** `url` with one more query parameter, whether or not it already has some. */
export const withQueryParam = (url: string, key: string, value: string): string =>
  `${url}${url.includes("?") ? "&" : "?"}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
